import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useOnboardingFormStore } from '@/lib/onboarding-form-store';
import { useAppForm } from '@/lib/use-app-form';
import type { Locale } from '@worldcoo/monolith/core';
import { Card, CardContent, CardHeader, CardTitle } from '@worldcoo/ui/components/card';
import { currencyKeys } from '@worldcoo/monolith/iso/4217';
import { step3Schema } from '@/lib/onboarding-schemas';
import { useUploadTexts } from './-components/use-upload-texts';

export const Route = createFileRoute('/ngos/onboarding/accreditation')({
	component: RouteComponent,
});

function RouteComponent() {
	const { t, i18n } = useLingui();
	const countryFieldTexts = {
		search: t`Search...`,
		popular: t`Popular`,
		allCountries: t`All countries`,
		noCountryFound: t`No country found.`,
	};
	const regionFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const provinceFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const navigate = useNavigate();
	const uploadTexts = useUploadTexts();
	const { formData, setFormData, setCurrentStep } = useOnboardingFormStore();

	const currencyOptions = currencyKeys.map((key) => ({ value: key, label: key }));

	const form = useAppForm({
		defaultValues: {
			donationCertificates: formData.donationCertificates,
			certifiedByFoundation: formData.certifiedByFoundation,
			certifiedByFoundationDescription: formData.certifiedByFoundationDescription,
			corporateVolunteering: formData.corporateVolunteering,
			corporateVolunteeringDescription: formData.corporateVolunteeringDescription,
			bankInfo: formData.bankInfo,
			bankCurrency: formData.bankCurrency,
			bankAccountName: formData.bankAccountName,
			bankAccountCertificate: formData.bankAccountCertificate,
			bankCountry: formData.bankCountry || formData.country,
			bankCity: formData.bankCity,
			bankRegion: formData.bankRegion || formData.region,
			bankProvince: formData.bankProvince || formData.province,
			bankPostalCode: formData.bankPostalCode,
			bankAddress: formData.bankAddress,
		},
		validators: {
			onSubmit: step3Schema,
		},
		onSubmit: async ({ value }) => {
			setFormData({ ...value, bankAccountCertificate: value.bankAccountCertificate });
			setCurrentStep(4);
			navigate({ to: '/ngos/onboarding/privacy', search: (prev) => prev });
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const action = (e.nativeEvent as SubmitEvent).submitter?.getAttribute('value');

		if (action === 'back') {
			const values = form.state.values;
			setFormData({ ...values, bankAccountCertificate: values.bankAccountCertificate });
			setCurrentStep(2);
			navigate({ to: '/ngos/onboarding/contacts', search: (prev) => prev });
			return;
		}

		form.handleSubmit();
	};

	return (
		<form id="onboarding-step-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
			{/* Accreditation Details */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Accreditation Details`}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<form.AppField
						name="certifiedByFoundation"
						children={(field) => <field.SwitchField label={t`Certified by Foundation`} position="right" />}
					/>
					<form.Subscribe selector={(state) => state.values.certifiedByFoundation}>
						{(certifiedByFoundation) => (
							<div
								className="grid transition-[grid-template-rows,opacity] duration-200"
								style={{
									gridTemplateRows: certifiedByFoundation ? '1fr' : '0fr',
									opacity: certifiedByFoundation ? 1 : 0,
								}}
							>
								<div className="-m-1 overflow-hidden p-1">
									<form.AppField
										name="certifiedByFoundationDescription"
										children={(field) => <field.TextareaField label={t`Foundation Certification Details`} />}
									/>
								</div>
							</div>
						)}
					</form.Subscribe>
					<form.AppField
						name="corporateVolunteering"
						children={(field) => <field.SwitchField label={t`Corporate Volunteering`} position="right" />}
					/>
					<form.Subscribe selector={(state) => state.values.corporateVolunteering}>
						{(corporateVolunteering) => (
							<div
								className="grid transition-[grid-template-rows,opacity] duration-200"
								style={{
									gridTemplateRows: corporateVolunteering ? '1fr' : '0fr',
									opacity: corporateVolunteering ? 1 : 0,
								}}
							>
								<div className="-m-1 overflow-hidden p-1">
									<form.AppField
										name="corporateVolunteeringDescription"
										children={(field) => <field.TextareaField label={t`Corporate Volunteering Details`} />}
									/>
								</div>
							</div>
						)}
					</form.Subscribe>
				</CardContent>
			</Card>

			{/* Donations Management */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Donations Management`}</CardTitle>
				</CardHeader>
				<CardContent>
					<form.AppField
						name="donationCertificates"
						children={(field) => <field.SwitchField label={t`Issue Donation Certificates`} position="right" />}
					/>
				</CardContent>
			</Card>

			{/* Funds Management */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Funds Management`}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<form.AppField
						name="bankInfo.wantSwiftAndCC"
						children={(field) => <field.SwitchField label={t`Use SWIFT/BIC instead of IBAN`} position="right" />}
					/>
					<form.Subscribe selector={(state) => state.values.bankInfo.wantSwiftAndCC}>
						{(wantSwift) => (
							<>
								<div
									className="grid transition-[grid-template-rows,opacity] duration-200"
									style={{
										gridTemplateRows: wantSwift ? '1fr' : '0fr',
										opacity: wantSwift ? 1 : 0,
									}}
								>
									<div className="-m-1 flex flex-col gap-6 overflow-hidden p-1">
										<form.AppField
											name="bankInfo.bankSwiftCode"
											children={(field) => <field.TextField label={t`SWIFT/BIC Code`} />}
										/>
										<form.AppField
											name="bankInfo.bankAccountNumber"
											children={(field) => <field.TextField label={t`Bank Account Number`} />}
										/>
									</div>
								</div>
								<div
									className="grid transition-[grid-template-rows,opacity] duration-200"
									style={{
										gridTemplateRows: !wantSwift ? '1fr' : '0fr',
										opacity: !wantSwift ? 1 : 0,
									}}
								>
									<div className="-m-1 overflow-hidden p-1">
										<form.AppField
											name="bankInfo.bankIBANCode"
											children={(field) => <field.TextField label={t`IBAN`} />}
										/>
									</div>
								</div>
							</>
						)}
					</form.Subscribe>
					<form.AppField
						name="bankCurrency"
						children={(field) => <field.SelectTextField label={t`Currency`} options={currencyOptions} />}
					/>
					<form.AppField
						name="bankAccountName"
						children={(field) => <field.TextField label={t`Account Holder Name`} />}
					/>
					<div>
						<div className="mt-2">
							<form.AppField
								name="bankAccountCertificate"
								children={(field) => (
									<field.DocumentUploadField
										{...uploadTexts.documentUpload}
										currentUrl={undefined}
										currentKey={undefined}
										label={t`Bank Account Certificate`}
										buttonLabel={t`Upload Certificate`}
									/>
								)}
							/>
						</div>
					</div>

					{/* Bank Address */}
					<form.AppField name="bankAddress" children={(field) => <field.TextField label={t`Bank Address`} />} />
					<div className="flex gap-4">
						<form.AppField
							name="bankCity"
							children={(field) => <field.TextField label={t`City`} className="w-full" />}
						/>
						<form.AppField
							name="bankPostalCode"
							children={(field) => <field.TextField label={t`Postal Code`} className="w-full" />}
						/>
					</div>
					<form.AppField
						name="bankCountry"
						listeners={{
							onChange: () => {
								form.setFieldValue('bankRegion', '', { dontValidate: true, dontUpdateMeta: true });
								form.setFieldValue('bankProvince', '', { dontValidate: true, dontUpdateMeta: true });
							},
						}}
						children={(field) => (
							<field.CountryField label={t`Country`} locale={i18n.locale as Locale} texts={countryFieldTexts} />
						)}
					/>
					<form.Subscribe
						selector={(state) => ({ bankCountry: state.values.bankCountry, bankRegion: state.values.bankRegion })}
					>
						{({ bankCountry, bankRegion }) => (
							<>
								<form.AppField
									name="bankRegion"
									validators={{ onChangeListenTo: ['bankCountry'] }}
									listeners={{
										onChange: () =>
											form.setFieldValue('bankProvince', '', { dontValidate: true, dontUpdateMeta: true }),
									}}
									children={(field) => (
										<field.RegionField label={t`Region`} country={bankCountry} texts={regionFieldTexts} />
									)}
								/>
								<form.AppField
									name="bankProvince"
									validators={{ onChangeListenTo: ['bankCountry', 'bankRegion'] }}
									children={(field) => (
										<field.ProvinceField
											label={t`Province`}
											region={bankRegion}
											country={bankCountry}
											texts={provinceFieldTexts}
										/>
									)}
								/>
							</>
						)}
					</form.Subscribe>
				</CardContent>
			</Card>
		</form>
	);
}
